/**
 * Core Tool Interfaces for SWEDevTools: Prism
 * 
 * This file defines the contract that all tools must implement.
 * Tools are the fundamental building blocks of the pipeline system.
 * 
 * @see docs/Research/DesignSpecsClaude.md Section 4.1
 */

import type { ReactElement, RefObject } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Type Definitions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The three types of tools in the system.
 * - transformer: Takes input, produces output (e.g., Base64 Encode)
 * - generator: Produces output without input (e.g., UUID Generator)
 * - visualizer: Renders visual output (e.g., JSON Tree View)
 */
export type ToolType = 'transformer' | 'generator' | 'visualizer';

/**
 * Configurable attribute for a tool.
 * Attributes define the options users can configure for each tool.
 */
export interface ToolAttribute {
  /** Internal key used in config (e.g., "indentSize") */
  name: string;
  /** Data type of the attribute */
  type: 'string' | 'number' | 'boolean' | 'select';
  /** Display label shown in UI (e.g., "Indent Size") */
  label: string;
  /** Default value when tool is first added */
  defaultValue: string | number | boolean;
  /** For string inputs: render as a multi-line text area */
  multiline?: boolean;
  /** For 'select' type: available options */
  options?: string[];
  /** Help text for tooltips */
  description?: string;
  /** Validation constraints for number types */
  validation?: {
    min?: number;
    max?: number;
    step?: number;
  };
}

// Import ISemanticType from the canonical source
import type { ISemanticType } from './ISemanticType';

// ─────────────────────────────────────────────────────────────────────────────
// Tool Execution Result
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Rich return type for transform/generate. Tools may return a plain string
 * (backward compat) or an object with optional dynamic output type.
 */
export interface ToolExecutionResult {
  output: string;
  outputType?: ISemanticType;
}

/**
 * Normalize a tool's raw return value to a consistent ToolExecutionResult.
 * Handles both legacy string returns and rich object returns.
 */
export function normalizeToolResult(
  raw: string | ToolExecutionResult,
  fallbackType?: ISemanticType,
): ToolExecutionResult {
  if (typeof raw === 'string') {
    return { output: raw, outputType: fallbackType };
  }
  return { output: raw.output, outputType: raw.outputType ?? fallbackType };
}

/**
 * Resolve the effective output type for a tool, checking (in priority order):
 *   1. Runtime result's dynamic outputType (from actual execution)
 *   2. Config-based prediction via tool.getOutputType(config)
 *   3. Static tool.outputType declaration
 */
export function resolveOutputType(
  tool: IToolBase,
  config?: Record<string, unknown>,
  runtimeResult?: ToolExecutionResult,
): ISemanticType | undefined {
  if (runtimeResult?.outputType) return runtimeResult.outputType;
  if (config && tool.getOutputType) {
    try {
      const predicted = tool.getOutputType(config);
      if (predicted) return predicted;
    } catch {
      // Fallback silently — bad predictor should not break render/save flows
    }
  }
  return tool.outputType;
}

// ─────────────────────────────────────────────────────────────────────────────
// Base Tool Interface
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Base interface for all tools.
 * Contains common metadata shared across all tool types.
 */
export interface IToolBase {
  /** Unique identifier (e.g., "json-format") */
  id: string;
  /** Display name (e.g., "JSON Formatter") */
  name: string;
  /** Short description for UI tooltips */
  description: string;
  /** Semantic version (e.g., "1.0.0") */
  version: string;
  /** Category of tool */
  type: ToolType;
  /** Configurable options */
  attributes: ToolAttribute[];
  /** Category for grouping in UI (e.g., "JSON", "Encoding") */
  category?: string;
  /** Keywords for search */
  keywords?: string[];
  
  /**
   * Declares what input types this tool can accept.
   * Used by the recommendation engine to suggest tools.
   */
  supportedInputTypes?: ISemanticType[];
  
  /**
   * Declares what output type this tool produces.
   * Used for pipeline validation and downstream tool suggestions.
   */
  outputType?: ISemanticType;
  
  /**
   * Optional: Predict output type from config WITHOUT executing.
   * Used by UI for pre-run layout hints and recommendations.
   * Falls back to static outputType if not provided.
   */
  getOutputType?(config: Record<string, unknown>): ISemanticType | undefined;

  /**
   * Optional method to check if this tool supports a given input.
   * More granular than supportedInputTypes - can inspect actual content.
   */
  supportsInput?(input: string, detectedType?: string): boolean;

  /**
   * Optional SEO metadata for generating standalone tool pages.
   * Tools with `seo` data get their own discoverable URL at `/tools/:slug`.
   */
  seo?: IToolSEO;
}

// ─────────────────────────────────────────────────────────────────────────────
// SEO Interfaces
// ─────────────────────────────────────────────────────────────────────────────

/**
 * SEO metadata for a single tool page.
 * Enables the tool to be published as a standalone, search-engine-optimized
 * page with its own URL, meta tags, and structured data.
 */
export interface IToolSEO {
  /** URL slug — lowercase, hyphenated, unique across all tools (e.g. "json-formatter") */
  slug: string;
  /** Page <title> override. Defaults to tool name if not set. */
  pageTitle?: string;
  /** Meta description for search results (max 160 chars). */
  metaDescription: string;
  /** Additional keywords for meta tags. */
  keywords: string[];
  /** Related tool IDs for internal linking on the SEO page. */
  relatedTools?: string[];
  /** Schema.org type hint for structured data. */
  schemaType?: 'WebApplication' | 'SoftwareApplication';
}

/**
 * SEO metadata for a saved pipeline preset.
 * Enables multi-tool pipelines to be published at `/pipelines/:slug`.
 */
export interface IPipelineSEO {
  /** URL slug — lowercase, hyphenated, unique across all pipelines. */
  slug: string;
  /** Page <title> override. */
  pageTitle?: string;
  /** Meta description for search results (max 160 chars). */
  metaDescription: string;
  /** Additional keywords for meta tags. */
  keywords: string[];
}

/**
 * Optional execution context passed to tools during pipeline runs.
 * Tools may ignore this entirely, but it enables pipeline-scoped features like variables.
 */
export interface ToolExecutionContext {
  /** The pipeline name, if any */
  pipelineName?: string;
  /** Step instance ID when executing inside a pipeline */
  stepId?: string;
  /** Pipeline-scoped variables */
  variables: Record<string, string>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tool Type-Specific Interfaces
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Transformer Tool - Takes input, produces output.
 * Most common tool type (e.g., Base64 Encode/Decode, JSON Format).
 *
 * TReturn defaults to `string` for backward compatibility. Tools that return
 * dynamic output types use `ITransformerTool<string | ToolExecutionResult>`.
 */
export interface ITransformerTool<TReturn extends string | ToolExecutionResult = string> extends IToolBase {
  type: 'transformer';
  
  /**
   * Transform input to output.
   * Returns string (common case) or ToolExecutionResult for dynamic output type.
   */
  transform(input: string, config: Record<string, unknown>, context?: ToolExecutionContext): Promise<TReturn>;
}

/**
 * Generator Tool - Produces output without input.
 * Examples: UUID Generator, Lorem Ipsum, Timestamp.
 * Can appear at any position in a pipeline.
 *
 * TReturn defaults to `string` for backward compatibility. Tools that return
 * dynamic output types use `IGeneratorTool<string | ToolExecutionResult>`.
 */
export interface IGeneratorTool<TReturn extends string | ToolExecutionResult = string> extends IToolBase {
  type: 'generator';
  
  /** Generators don't consume input */
  supportedInputTypes?: undefined;
  
  /**
   * Generate output without requiring input.
   * Returns string (common case) or ToolExecutionResult for dynamic output type.
   */
  generate(config: Record<string, unknown>, context?: ToolExecutionContext): Promise<TReturn>;
}

/**
 * Props passed to visualizer render functions.
 */
export interface VisualizerRenderContext {
  /** The input data to visualize */
  input: string;
  /** User configuration */
  config: Record<string, unknown>;
  /** Optional config updater for visualizer-only state (e.g., search). */
  onConfigChange?: (config: Record<string, unknown>) => void;
  /** Container ref for direct DOM access */
  containerRef: RefObject<HTMLDivElement>;
  /** Responsive width */
  containerWidth: number;
  /** Responsive height */
  containerHeight: number;
  /** Current app theme */
  theme: 'light' | 'dark';
  /**
   * Callback for editable visualizers to signal data changes.
   * When called, the pipeline re-executes from this step forward.
   */
  onEdit?: (newData: string) => void;
  /** Whether this visualizer is currently in edit mode. */
  editMode?: boolean;
}

/**
 * Visualizer Tool - Renders visual output.
 * Examples: JSON Tree View, Diff Viewer, Hex View.
 */
export interface IVisualizerTool extends IToolBase {
  type: 'visualizer';
  
  /**
   * Render a React element to visualize the input.
   * @param context - Contains input, config, and container information
   * @returns React element to render
   */
  render(context: VisualizerRenderContext): ReactElement;
  
  /**
   * Optional: Convert visualization back to text.
   * If provided, allows visualizer output to flow to next pipeline step.
   */
  toText?(input: string, config: Record<string, unknown>): string;

  /**
   * Whether this visualizer supports inline editing.
   * When true, the UI shows an edit-mode toggle and passes onEdit/editMode
   * to the render context.
   */
  editable?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Union Type and Type Guards
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Union type representing any tool.
 * Uses widened generic params so the engine handles both string and rich returns.
 */
export type ITool =
  | ITransformerTool<string | ToolExecutionResult>
  | IGeneratorTool<string | ToolExecutionResult>
  | IVisualizerTool;

/**
 * Type guard to check if a tool is a Transformer.
 */
export function isTransformer(tool: ITool): tool is ITransformerTool<string | ToolExecutionResult> {
  return tool.type === 'transformer';
}

/**
 * Type guard to check if a tool is a Generator.
 */
export function isGenerator(tool: ITool): tool is IGeneratorTool<string | ToolExecutionResult> {
  return tool.type === 'generator';
}

/**
 * Type guard to check if a tool is a Visualizer.
 */
export function isVisualizer(tool: ITool): tool is IVisualizerTool {
  return tool.type === 'visualizer';
}

/**
 * Get default config values from a tool's attributes.
 */
export function getDefaultConfig(tool: ITool): Record<string, unknown> {
  const config: Record<string, unknown> = {};
  for (const attr of tool.attributes) {
    config[attr.name] = attr.defaultValue;
  }
  return config;
}

